// Convert a camelCase token name into its kebab-case CSS custom property
// suffix, e.g. `borderRadius` -> `border-radius`.
const kebabCase = (key) => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

const isPlainObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Nested theme groups exposed as CSS custom properties. Emitting these as
// variables (rather than reading them out of Redux in each component) is what
// lets a single partner token reach every seatmap design version: the legacy
// branches consume them through the `gr-*` utility classes in tailwind.css,
// and the redesign branches consume the same variables directly from their
// inline styles via `var(--token, <current literal>)`.
//
// Only tokens the partner actually sets are emitted, so an unset token leaves
// the variable undefined and every call site falls back to the literal it
// used before, keeping unthemed output byte-identical.
const NESTED_TOKEN_GROUPS = [
  ['button-primary', (theme) => theme.buttons && theme.buttons.primary],
  ['button-secondary', (theme) => theme.buttons && theme.buttons.secondary],
  // The redesign paints passenger avatars, active rows and emphasis text in a
  // single neutral (#111827 today). theme.accent is that colour, with
  // `contrastColor` for anything sitting on top of it. Kept separate from the
  // CTA so a partner can recolour buttons without recolouring list rows.
  ['accent', (theme) => theme.accent],
  // Only the presentational fields become variables. `hide`, `urlTemplate`
  // and `overrides` drive resolveAirlineLogo() in JS, and emitting them here
  // would put a partner's URL template on the document root for nothing.
  [
    'logo',
    (theme) =>
      theme.logos && {
        borderRadius: theme.logos.borderRadius,
        size: theme.logos.size,
      },
  ],
  ['footer', (theme) => theme.footer],
  ['legend', (theme) => theme.legend],
  ['select', (theme) => theme.select],
  ['seat', (theme) => theme.seat],
  ['overlay', (theme) => theme.overlay],
  ['close-button', (theme) => theme.closeButton],
  ['premium-badge', (theme) => theme.premiumBadge],
];

export const mapNestedTheme = (variables = {}) => {
  const vars = {};
  NESTED_TOKEN_GROUPS.forEach(([prefix, pick]) => {
    const group = pick(variables);
    if (!isPlainObject(group)) return;
    Object.keys(group).forEach((key) => {
      const value = group[key];
      if (value === undefined || value === null || value === '') return;
      // Nested maps (premiumBadge.labels, logos.overrides) are read directly
      // in JS; only scalars become custom properties.
      if (typeof value === 'object') return;
      vars[`--gordian-${prefix}-${kebabCase(key)}`] = String(value);
    });
  });
  return vars;
};

export const mapTheme = (variables) => {
  return {
    // colors
    '--color-gordian-primary': variables.primary || '',
    '--color-gordian-secondary': variables.secondary || '',
    '--color-gordian-text-primary': variables.textPrimary || '',
    '--color-gordian-button-text': variables.buttonText || '',
    '--color-gordian-text-secondary': variables.textSecondary || '',
    '--color-gordian-link': variables.link || '',
    '--color-gordian-title': variables.title || '',
    '--color-gordian-alert': variables.alert || '',
    '--color-gordian-warning': variables.warning || '',
    '--color-gordian-success': variables.success || '',
    '--color-gordian-gray_10': variables.gray_10 || '',
    '--color-gordian-gray_15': variables.gray_15 || '',
    '--color-gordian-gray_20': variables.gray_20 || '',
    '--color-gordian-gray_50': variables.gray_50 || '',
    '--color-gordian-gray_70': variables.gray_70 || '',
    '--color-gordian-gray_100': variables.gray_100 || '',
    // elements
    '--action-button-border-radius': variables.actionButtonBorderRadius || '', // this applies to <Button /> React elements
    '--button-border-radius': variables.buttonBorderRadius || '', // this applies to _all_ <button> elements
    '--detail-container-desktop-border':
      variables.detailContainerDesktopBorder || '',
    '--detail-container-desktop-border-radius':
      variables.detailContainerDesktopBorderRadius || '',
    '--detail-container-mobile-border':
      variables.detailContainerMobileBorder || '',
    '--detail-container-mobile-border-radius':
      variables.detailContainerMobileBorderRadius || '',
    '--legend-border': variables.legendBorder || '',
    '--legend-border-radius': variables.legendBorderRadius || '',
    // Nested token groups (buttons, footer, legend, select, seat, overlay,
    // close button, premium badge). See NESTED_TOKEN_GROUPS above.
    ...mapNestedTheme(variables),
  };
};

export const applyTheme = (theme) => {
  const themeObject = mapTheme(theme);
  if (!themeObject) return;

  const root = document.documentElement;

  Object.keys(themeObject).forEach((property) => {
    if (property === 'name') {
      return;
    }
    root.style.setProperty(property, themeObject[property]);
  });
};

export const extend = (extending = {}, newTheme = {}) => {
  const merged = { ...extending, ...newTheme };

  // Merge any group that is an object on both sides one level deep, so a
  // partner overriding a single token does not wipe out its siblings.
  Object.keys(merged).forEach((key) => {
    if (isPlainObject(extending[key]) && isPlainObject(newTheme[key])) {
      merged[key] = { ...extending[key], ...newTheme[key] };
    }
  });

  // `buttons` nests one level further (buttons.primary / buttons.secondary),
  // so merge its variants individually rather than replacing them.
  if (isPlainObject(extending.buttons) && isPlainObject(newTheme.buttons)) {
    const buttons = { ...extending.buttons, ...newTheme.buttons };
    Object.keys(buttons).forEach((variant) => {
      if (
        isPlainObject(extending.buttons[variant]) &&
        isPlainObject(newTheme.buttons[variant])
      ) {
        buttons[variant] = {
          ...extending.buttons[variant],
          ...newTheme.buttons[variant],
        };
      }
    });
    merged.buttons = buttons;
  }

  // Always present, even when neither theme defines them: call sites index
  // into these directly rather than optional-chaining.
  merged.seats = { ...extending.seats, ...newTheme.seats };
  merged.bags = { ...extending.bags, ...newTheme.bags };
  merged.seatmap = { ...extending.seatmap, ...newTheme.seatmap };

  return merged;
};

export const getTriggerStyles = (theme, triggerType) => {
  const defaultStyles = {
    border: '2px solid #DBE4EA',
    fontSize: '16px',
    fontFamily: 'Roboto, sans-serif',
    margin: '20px',
  };

  const themeStyles = theme?.[triggerType] || {};

  return {
    ...defaultStyles,
    ...(themeStyles.background && { background: themeStyles.background }),
    ...(themeStyles.border && { border: themeStyles.border }),
    ...(themeStyles.borderRadius && { borderRadius: themeStyles.borderRadius }),
    ...(themeStyles.color && { color: themeStyles.color }),
    ...(themeStyles.fontFamily && { fontFamily: themeStyles.fontFamily }),
    ...(themeStyles.fontSize && { fontSize: themeStyles.fontSize }),
    ...(themeStyles.fontStyle && { fontStyle: themeStyles.fontStyle }),
    ...(themeStyles.fontWeight && { fontWeight: themeStyles.fontWeight }),
    ...(themeStyles.margin && { margin: themeStyles.margin }),
  };
};

export const getTriggerButtonStyles = (theme, triggerType) => {
  const defaultStyles = {
    width: '200px',
    height: '50px',
    border: '0 solid #000000',
    borderRadius: '8px',
    color: '#ffffff',
    background: '#2990e6',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '16px',
    lineHeight: '140%',
    textAlign: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    alignItems: 'center',
  };

  const themeStyles = theme?.[triggerType] || {};
  const buttonStyles = themeStyles?.button || {};

  return {
    ...defaultStyles,
    ...(buttonStyles.background && { background: buttonStyles.background }),
    ...(buttonStyles.border && { border: buttonStyles.border }),
    ...(buttonStyles.borderRadius && {
      borderRadius: buttonStyles.borderRadius,
    }),
    ...(buttonStyles.color && { color: buttonStyles.color }),
    ...(buttonStyles.fontFamily && { fontFamily: buttonStyles.fontFamily }),
    ...(buttonStyles.fontSize && { fontSize: buttonStyles.fontSize }),
    ...(buttonStyles.fontStyle && { fontStyle: buttonStyles.fontStyle }),
    ...(buttonStyles.fontWeight && { fontWeight: buttonStyles.fontWeight }),
  };
};
